import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await http.post("/auth/forgot", { email });
      setSent(true);
      // In dev/no-email mode, the API returns the token so the user can complete the flow.
      if (data?.reset_token) setResetToken(data.reset_token);
      toast.success("If that email is in our records, a reset link is on its way.");
    } catch {
      // Always show the same friendly result (no enumeration)
      setSent(true);
    } finally { setLoading(false); }
  };

  return (
    <Layout>
      <section className="bg-[#050505] min-h-[80vh] py-24">
        <div className="max-w-[520px] mx-auto px-6 lg:px-10">
          <div className="font-ui text-[11px] tracking-[0.32em] text-[#C9A055] mb-3">Forgot Password</div>
          <h1 className="font-display text-5xl text-[#F4F1EA] mb-2">RESET ACCESS.</h1>
          <p className="font-script text-[#C9A055] text-2xl mb-8">We'll send you a link.</p>

          {sent ? (
            <div className="border border-emerald-700/40 bg-emerald-900/10 p-6">
              <CheckCircle2 size={36} className="text-emerald-400 mb-3"/>
              <h2 className="font-display text-2xl text-[#F4F1EA] mb-2">CHECK YOUR INBOX.</h2>
              <p className="font-body text-[#B5B0A7] mb-4">If your email is registered, you'll receive a password reset link shortly.</p>
              {resetToken && (
                <div className="border border-[#C9A055]/40 bg-[#C9A055]/10 p-4 mt-4">
                  <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-2">Dev mode — email delivery is not yet active</div>
                  <p className="font-body text-sm text-[#F4F1EA] mb-3">Use this link directly while Resend isn't configured:</p>
                  <Link
                    to={`/reset-password?token=${resetToken}`}
                    data-testid="dev-reset-link"
                    className="btn-gold inline-flex text-[10px]"
                  >
                    Continue to Reset Password
                  </Link>
                </div>
              )}
              <div className="mt-6">
                <Link to="/login" className="btn-ghost">← Back to sign in</Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
              <input
                data-testid="forgot-email"
                required
                type="email"
                placeholder="Your email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="stc-input"
              />
              <button data-testid="forgot-submit" disabled={loading} className="btn-gold w-full">
                {loading ? "Sending…" : "Send reset link"}
              </button>
              <div className="font-ui-tight text-sm text-[#B5B0A7]">
                Remembered it? <Link to="/login" className="text-[#C9A055] hover:text-[#E0BC72]">Sign in</Link>
              </div>
            </form>
          )}
        </div>
      </section>
    </Layout>
  );
}
